import { MutableRefObject, useContext, useEffect, useRef, useState } from "react";

import './CCGTPage.scss';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { Dropdown } from "primereact/dropdown";
import { classNames } from "primereact/utils";
import { Calendar, CalendarChangeParams } from "primereact/calendar";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import {
    CCGTdetailsHistory,
    CCGTdetailsRealTime,
    CCGTMSDreport,
    DataPlantApiFactory,
    GlobalApiFactory,
    HomePlant,
    TimeSeriesItem
} from "../../Services/typescript-fetch-client";
import ProfiliCaricoContext, { MainContext } from "../../Context/ProfiliCaricoContext";
import CustomSidebar from "../../Components/CustomSidebar/CustomSidebar";
import MixedPlotlyChartComponent from "../../Components/ChartComponent2/MixedPlotlyChartComponent";
import PlotsSharedLegend, {LegendSerie} from "../../Components/PlotsSharedLegend/PlotsSharedLegend";
import {DateTime} from "luxon";
import {ZoomElement, zoomEquals} from "../../Models/ZoomElement";
import { useUrlStateCCGT } from "./useUrlStateCCGT";
import { useZoom } from "../../Utils/useZoom";
import { useNavigate } from "react-router-dom";

/**
 * enable verbose logging of request lifecycle
 */
const VERBOSE: boolean = false;

type Props = {
    plantType: HomePlant.TypeEnum,
};

/**
 * a canceller allows to cancel the currently running api requests when they should be replaced with new ones. The id is an incremental identifier and is used for debugging purposes.
 */
type Canceller = ((reason?: unknown) => void) & { id?: number };

const CCGTPage = ({ plantType }: Props) => {
    const {
        plantName,
        dateSelected,
        setDateSelected,
        dateShown,
        isRealTime
    } = useUrlStateCCGT();

    const [ccgtDetailsRealTimeAll, setCCGTdetailsRealTimeAll] = useState<CCGTdetailsRealTime[]>([]);
    const [MSDobj, setMSDobj] = useState<any[]>([]);
    const [ccgtDetailsHistoryAll, setCCGTdetailsHistoryAll] = useState<CCGTdetailsHistory[]>([]);
    const [activeSeries, setActiveSeries] = useState<Map<string, LegendSerie>>(new Map());

    const { zoom, setZoom, resetZoom, zoomBack, backToInitialZoom } = useZoom();

    let containerRef = useRef<HTMLDivElement>(null);
    const context = useContext(ProfiliCaricoContext);
    let cc: CCGTMSDreport = { label: undefined, status: undefined };
    const nav = useNavigate();

    useEffect(() => {
        setActiveSeries(new Map([
            ['Energia Prodotta', {name: "Energia Prodotta", active: true, color: "#97D700", controlledSeries: ["Energia Prodotta", "BAD"]}],
			['Energia Da Produrre', {name: "Energia Da Produrre", active: true, color: "#f3af09", controlledSeries: ["Energia Da Produrre"]}],
            ['PV', {name: "PV", active: false, color: "#f700ff", controlledSeries: ["PV"]}],
            ['SB', {name: "SB", active: true, color: "#516cb7", controlledSeries: ["SB"]}],
            ['PVM', {name: "PVM", active: true, color: "#e7292e", controlledSeries: ["PVM"]}],
            ['PF', {name: "PF", active: false, color: "#c9a0dc", controlledSeries: ["PF"]}],
            ['PsMin1', {name: "PsMin1", active: true, color: "#868686", controlledSeries: ["PsMin1"], showLegend: false}],
            ['PsMin2', {name: "PsMin2", active: true, color: "#868686", controlledSeries: ["PsMin2"], showLegend: false}],
            ['PsMax1', {name: "PsMax1", active: true, color: "#868686", controlledSeries: ["PsMax1"], showLegend: false}],
            ['PsMax2', {name: "PsMax2", active: true, color: "#868686", controlledSeries: ["PsMax2"], showLegend: false}]
        ]));
    }, [plantName]);

    const cancel = useRef<Canceller | null>(null);

    useEffect(() => {
        if (context.accessToken) {
            GlobalApiFactory({accessToken: context.accessToken!, apiKey: "123"}).globalAllPlantsGet().then((homePlantsAPI: HomePlant[]) => {
                let plants = homePlantsAPI.filter(p => p.type === (plantType as HomePlant.TypeEnum));
                let plantNames: string[] = [];
                plants.forEach(p => p.plantName ? plantNames.push(p.plantName) : null);
                if (plantName && !plantNames.includes(plantName)) {
                    nav("/*")
                }
            }).catch((e) => {
                if (e instanceof InteractionRequiredAuthError)
                    context.doAcquireTokenRedirect();
                else
                    context.showMsg("Errore", "Si è verificato un errore inaspettato nella lettura degli impianti", "error")
            });
        }
    }, [context, nav, plantName, plantType])

    useEffect((): Canceller | void => {
        if (context.accessToken) {
            const { newCancel, signal, setFinished } = createCancel(cancel)
            if (isRealTime) {
                refreshRealtimeData(context, plantName, setMSDobj, setCCGTdetailsRealTimeAll, setCCGTdetailsHistoryAll, setZoom, signal, setFinished);
                const refreshTimer = setInterval(() => { refreshRealtimeData(context, plantName, setMSDobj, setCCGTdetailsRealTimeAll, setCCGTdetailsHistoryAll, setZoom, signal, setFinished) }, 300000);//5 mins
                return () => {
                    newCancel("at CCGTPage useEffect realtime cleanup, cancelling outdated requests during cleanup");
                    if (refreshTimer != null) {
                        clearInterval(refreshTimer);
                    }
                }
            } else {
                refreshHistoryData(context, plantName, setCCGTdetailsRealTimeAll, setCCGTdetailsHistoryAll, dateShown, setZoom, signal, setFinished);
                return () => newCancel("at CCGTPage useEffect history cleanup, cancelling outdated requests during cleanup");
            }
        }
    }, [context.accessToken, dateShown, isRealTime, plantName, plantType]);

    useEffect(() => {
        resetZoom()
    }, [dateShown, plantName]);

    const setSeriesStatus = (name: string, active: boolean) => {
        setActiveSeries((as: Map<string, LegendSerie>) => {
            let temp = new Map(as);
            if(temp.has(name)) {
                let el: LegendSerie = temp.get(name)!;
                temp.set(name, {
                    ...el,
                    active: active
                });
            }
            return temp;
        });
    };

    const monthNavigatorTemplate = (e: any) => {
        return <Dropdown value={e.value} options={e.options} onChange={(event) => e.onChange(event.originalEvent, event.value)} style={{ lineHeight: 1 }} />;
    }

    const yearNavigatorTemplate = (e: any) => {
        return <Dropdown value={e.value} options={e.options} onChange={(event) => e.onChange(event.originalEvent, event.value)} className="ml-2" style={{ lineHeight: 1 }} />;
    }

    const getMSDOrder = (msdLabel: string) => {
        if(typeof msdLabel == "undefined"){
            return -1;
        }
        return Number(msdLabel[msdLabel.length -1]);
    };

    const refreshData = (plantName: string | undefined) => {
        const { signal, setFinished } = createCancel(cancel);
        if (isRealTime)
            refreshRealtimeData(context, plantName, setMSDobj, setCCGTdetailsRealTimeAll, setCCGTdetailsHistoryAll, setZoom, signal, setFinished);
        else
            refreshHistoryData(context, plantName, setCCGTdetailsRealTimeAll, setCCGTdetailsHistoryAll, dateShown, setZoom, signal, setFinished);
    };

    const forceRealTimeMode = () => {
        const today = DateTime.now().startOf('day').toJSDate();
        setDateSelected([today, today]);
    };

    const status = (rowData: any, item: any) => {
        switch (rowData[item.field]) {
            case CCGTMSDreport.StatusEnum.LoadedStatus:
                return (<div className={classNames('loadedStatus')}>Caricato</div>);
            case CCGTMSDreport.StatusEnum.OnWaitingStatus:
                return (<div className={classNames('onWaitingStatus')}>In attesa</div>);
            case CCGTMSDreport.StatusEnum.StopStatus:
                return (<div className={classNames('stopStatus')}>Assente</div>);
            default:
                return (<div className={classNames('error')}>Errore</div>);
        }
    };

    return <div ref={containerRef} className="with-footer">
            <CustomSidebar refreshAction={() => refreshData(plantName)} container={containerRef}>
                <>{<h3 className="m-2">Impostazioni CCGT</h3>}
                    <div className="flex justify-content-start m-2">
                        <Tag severity={isRealTime ? "success" : "warning"} rounded>{isRealTime ? "Modalità Realtime" : "Modalità Storico"}</Tag>
                    </div>
                    <div className="flex justify-content-start mb-2 mt-2 history-picker-container">
                        <Calendar placeholder="Date range" className="history-picker" value={dateSelected as Date | Date[]} onChange={(e: CalendarChangeParams) => { setDateSelected(e.value); }} monthNavigator yearNavigator yearRange="2010:2030"
                            monthNavigatorTemplate={monthNavigatorTemplate} yearNavigatorTemplate={yearNavigatorTemplate} showIcon selectionMode="range" readOnlyInput locale="it" />
                    </div>
                    <div className="flex justify-content-end">
                        <div className="flex align-items-start">
                            <Button label="Realtime" icon="pi pi-clock" onClick={forceRealTimeMode} />
                        </div>
                    </div>

                    <PlotsSharedLegend availableSeries={activeSeries} title="Dati Visualizzati" orientation="vertical" serieSetVisibility={setSeriesStatus} />
                </>
            </CustomSidebar>
            <div className="grid no-padding">
                {isRealTime && ccgtDetailsRealTimeAll.map((ccgtDetailsRealTime: CCGTdetailsRealTime) => {
                    return <div className={(ccgtDetailsRealTimeAll.length === 1 ? "col-12" : "col-6") + " graphPanel"} key={ccgtDetailsRealTime.id} >
                        <MixedPlotlyChartComponent isSingleGraph={ccgtDetailsRealTimeAll.length === 1} isPlant={true}
                                                   url={ccgtDetailsRealTime.plantName ? "/" + plantType + "/" + ccgtDetailsRealTime.plantName : null}
                            showDetailKpis={isRealTime && typeof plantName === "string"}
                                                   plantName={ccgtDetailsRealTime.plantName!}
                                                   fullScale={ccgtDetailsRealTime.fullScale} type={HomePlant.TypeEnum.Ccgt}
                                                   ccgtDetailsRealTime={ccgtDetailsRealTime}
                                                   visibleSeries={activeSeries}
                                                   maxMSDloaded={ccgtDetailsRealTime.msDreport
                                                       ?.filter(msd => msd.status !== CCGTMSDreport.StatusEnum.OnWaitingStatus)
                                                       ?.reduce((a, b) => (getMSDOrder(b.label!) > getMSDOrder(a.label!)) ? b : a, cc).label ?? undefined}
                            zoom={zoom!} setZoom={setZoom} zoomBack={zoomBack} backToInitialZoom={backToInitialZoom} >
                            {typeof plantName === "string" && <PlotsSharedLegend availableSeries={activeSeries} orientation="horizontal" serieSetVisibility={setSeriesStatus} />}
                        </MixedPlotlyChartComponent>
                    </div>
                })}
                {!isRealTime && ccgtDetailsHistoryAll.map((ccgtDetailsHistory: CCGTdetailsHistory) => {
                    return <div className={(ccgtDetailsHistoryAll.length === 1 ? "col-12" : "col-6") + " graphPanel"} key={ccgtDetailsHistory.id}>
                        <MixedPlotlyChartComponent isSingleGraph={ccgtDetailsHistoryAll.length === 1} isPlant={true}
                                                   url={ccgtDetailsHistory.plantName ? "/" + plantType + "/" + ccgtDetailsHistory.plantName : null}
                            showDetailKpis={isRealTime && typeof plantName === "string"}
                                                   plantName={ccgtDetailsHistory.plantName!}
                                                   fullScale={ccgtDetailsHistory.fullScale} type={HomePlant.TypeEnum.Ccgt}
                                                   ccgtDetailsHistory={ccgtDetailsHistory}
                                                   visibleSeries={activeSeries}
                            zoom={zoom!} setZoom={setZoom} zoomBack={zoomBack} backToInitialZoom={backToInitialZoom} >
                            {typeof plantName === "string" && <PlotsSharedLegend availableSeries={activeSeries} orientation="horizontal" serieSetVisibility={setSeriesStatus} />}
                        </MixedPlotlyChartComponent>
                    </div>
                })}
            </div>
            {typeof plantName !== "string" && <PlotsSharedLegend availableSeries={activeSeries} orientation="horizontal" serieSetVisibility={setSeriesStatus} />}
            {isRealTime && !context.inProgressApi && (<div className="CCGTcard">
                        <DataTable value={MSDobj} responsiveLayout="scroll" className="dataTable" id="msd-table">
                            <Column field="plantName" header="Impianto" />
                            <Column field="MSD1" header="MSD1" body={status} />
                            <Column field="MSD2" header="MSD2" body={status} />
                            <Column field="MSD3" header="MSD3" body={status} />
                            <Column field="MSD4" header="MSD4" body={status} />
                            <Column field="MSD5" header="MSD5" body={status} />
                            <Column field="MSD6" header="MSD6" body={status} />
                        </DataTable>
                    </div>)}
        </div>;
}

function createCancel(cancel: MutableRefObject<Canceller | null>
) {
    cancel.current?.("at CCGTPage createCancel, cancelling outdated requests before starting new ones");
    const abortController = new AbortController();
    const { signal } = abortController;
    const newCancel = abortController.abort.bind(abortController) as Canceller;
    newCancel.id = (cancel.current?.id ?? 0) + 1;
    VERBOSE && signal.addEventListener("abort", (message) => {
        console.log("at CCGTPage createCancel, received abort signal. id:", newCancel.id, "reason:", (message.target as { reason?: string }).reason);
    })
    cancel.current = newCancel;
    const setFinished = () => {
        VERBOSE && console.log("at CCGTPage createCancel setFinished, finished request with id:", newCancel.id);
        if (cancel.current === newCancel) {
            cancel.current = () => {
                VERBOSE && console.log("at CCGTPage createCancel cancel, no running request")
            };
            cancel.current.id = newCancel.id;
        }
    }
    return { newCancel, signal, setFinished }
}

function refreshHistoryData(context: MainContext, plantName: string | undefined,
    setCCGTdetailsRealTimeAll: React.Dispatch<React.SetStateAction<CCGTdetailsRealTime[]>>,
    setCCGTdetailsHistoryAll: React.Dispatch<React.SetStateAction<CCGTdetailsHistory[]>>,
    dateShown: DateTime[],
    setZoom: React.Dispatch<React.SetStateAction<ZoomElement | null>>,
    signal: AbortSignal,
    setFinished: () => void) {
    if (context.accessToken) {
        setCCGTdetailsRealTimeAll([]);
        if (typeof plantName === "string") {
            if (context.accessToken) {
                context.setInProgressApi!(true);
                DataPlantApiFactory({ accessToken: context.accessToken!, apiKey: "123" }).plantsCcgtByNameHistoryGet(plantName, dateShown[0].toISODate(), dateShown[1].toISODate(), { signal }).then((CCGTdetailsHistoryAPI: CCGTdetailsHistory) => {
                    setCCGTdetailsHistoryAll([CCGTdetailsHistoryAPI]);

                    let allDates = ([] as TimeSeriesItem[]).concat(CCGTdetailsHistoryAPI.series!.pv ?? [],
                        CCGTdetailsHistoryAPI.series!.psMax1 ?? [], CCGTdetailsHistoryAPI.series!.psMax2 ?? [], CCGTdetailsHistoryAPI.series!.psMin1 ?? [], CCGTdetailsHistoryAPI.series!.psMin2 ?? [],
                        CCGTdetailsHistoryAPI.series!.pf ?? [], CCGTdetailsHistoryAPI.series!.pFfromDWH ?? [], CCGTdetailsHistoryAPI.series!.prodotto ?? [], CCGTdetailsHistoryAPI.series!.pvm ?? [])
                        .map(v => DateTime.fromISO(v.dateKey! as unknown as string));
                    setZoom({
                        x: [DateTime.min(...allDates),
                            DateTime.max(...allDates)],
                        y: [0, CCGTdetailsHistoryAPI.fullScale]
                    });
                    
                    context.setInProgressApi!(false);
                }).catch((e) => {
                    context.setInProgressApi!(false);
                    if (e instanceof InteractionRequiredAuthError) {
                        context.doAcquireTokenRedirect();
                    }
                    else{
                        console.log(e);
                    }
                });
            }
        } else {
            if (context.accessToken) {
                context.setInProgressApi!(true);
                DataPlantApiFactory({ accessToken: context.accessToken!, apiKey: "123" }).plantsCcgtAllHistoryGet(dateShown[0].toISODate(), dateShown[1].toISODate(), { signal }).then((CCGTdetailsHistoryAPI: CCGTdetailsHistory[]) => {
                    CCGTdetailsHistoryAPI.sort((a, b) => { return a.order === b.order ? a.plantName!.localeCompare(b.plantName!) : a.order > b.order ? 1 : -1; });
                    setCCGTdetailsHistoryAll(CCGTdetailsHistoryAPI);

                    let allDates = ([] as TimeSeriesItem[]).concat(CCGTdetailsHistoryAPI[0].series!.pv ?? [],
                        CCGTdetailsHistoryAPI[0].series!.psMax1 ?? [], CCGTdetailsHistoryAPI[0].series!.psMax2 ?? [], CCGTdetailsHistoryAPI[0].series!.psMin1 ?? [], CCGTdetailsHistoryAPI[0].series!.psMin2 ?? [],
                        CCGTdetailsHistoryAPI[0].series!.pf ?? [], CCGTdetailsHistoryAPI[0].series!.pFfromDWH ?? [], CCGTdetailsHistoryAPI[0].series!.prodotto ?? [], CCGTdetailsHistoryAPI[0].series!.pvm ?? [])
                        .map(v => DateTime.fromISO(v.dateKey! as unknown as string));
                    setZoom({
                        x: [DateTime.min(...allDates),
                            DateTime.max(...allDates)],
                        y: [0, CCGTdetailsHistoryAPI[0].fullScale]
                    });
                    
                    context.setInProgressApi!(false);
                    VERBOSE && console.log("at CCGTPage refreshHistoryData, finished. plantName:", plantName, "from:", dateShown[0].toISODate(), "to", dateShown[1].toISODate())
                }).catch((e) => {
                    context.setInProgressApi!(false);
                    if (e instanceof InteractionRequiredAuthError) {
                        context.doAcquireTokenRedirect();
                    } else if (e.name === "AbortError") {
                        VERBOSE && console.warn("at CCGTPage refreshHistoryData, cancelled. plantName:", plantName, "from:", dateShown[0].toISODate(), "to", dateShown[1].toISODate(), e);
                    } else {
                        console.error("at CCGTPage refreshHistoryData, error. plantName:", plantName, "from:", dateShown[0].toISODate(), "to", dateShown[1].toISODate(), e);
                    }
                }).finally(setFinished);
            }
        }
    }
}

function refreshRealtimeData(context: MainContext, plantName: string | undefined, setMSDobj: React.Dispatch<React.SetStateAction<any>>,
    setCCGTdetailsRealTimeAll: React.Dispatch<React.SetStateAction<CCGTdetailsRealTime[]>>,
    setCCGTdetailsHistoryAll: React.Dispatch<React.SetStateAction<CCGTdetailsHistory[]>>,
    setZoom: React.Dispatch<React.SetStateAction<ZoomElement | null>>,
    signal: AbortSignal,
    setFinished: () => void) {
    if (context.accessToken) {
        setCCGTdetailsHistoryAll([]);
        context.setInProgressApi!(true);
        if (typeof plantName === "string") {
            DataPlantApiFactory({ accessToken: context.accessToken!, apiKey: "123" }).plantsCcgtByNameGet(plantName, { signal }).then((ccgtDetailsRealTimeAPI: CCGTdetailsRealTime[]) => {
                setCCGTdetailsRealTimeAll(ccgtDetailsRealTimeAPI);

                let allDates = ([] as TimeSeriesItem[]).concat(ccgtDetailsRealTimeAPI[0].series!.pv ?? [],
                    ccgtDetailsRealTimeAPI[0].series!.psMax1 ?? [], ccgtDetailsRealTimeAPI[0].series!.psMax2 ?? [], ccgtDetailsRealTimeAPI[0].series!.psMin1 ?? [], ccgtDetailsRealTimeAPI[0].series!.psMin2 ?? [],
                    ccgtDetailsRealTimeAPI[0].series!.pf ?? [], ccgtDetailsRealTimeAPI[0].series!.pFfromDWH ?? [], ccgtDetailsRealTimeAPI[0].series!.prodotto ?? [], ccgtDetailsRealTimeAPI[0].series!.pvm ?? [])
                    .map(v => DateTime.fromISO(v.dateKey! as unknown as string));
                setZoom({
                    x: [DateTime.min(...allDates),
                        DateTime.max(...allDates)],
                    y: [0, ccgtDetailsRealTimeAPI[0].fullScale]
                });
                
                let MSDobj0: any = ccgtDetailsRealTimeAPI.map((ccgtDetailsRealTime: any) => {
                    let obj: any = { 'plantName': ccgtDetailsRealTime.plantName };
                    ccgtDetailsRealTime.msDreport!.forEach((msd: any) => { obj[msd.label] = msd.status; });
                    return obj;
                });
                setMSDobj(MSDobj0);
                context.setInProgressApi!(false);
                VERBOSE && console.log("at CCGTPage refreshReatimeData, finished. plantName:", plantName)
            }).catch((e: any) => {
                context.setInProgressApi!(false);
                if (e instanceof InteractionRequiredAuthError) {
                    context.doAcquireTokenRedirect();
                }
                else{
                    console.error("at CCGTPage refreshReatimeData, error. plantName:", plantName, e);
                }
            });
        } else {
            DataPlantApiFactory({
                accessToken: context.accessToken!,
                apiKey: "123"
            }).plantsCcgtAllGet({ signal }).then((CCGTdetailsRealTimeAPI: CCGTdetailsRealTime[]) => {
                CCGTdetailsRealTimeAPI.sort((a, b) => {
                    return a.order === b.order ? a.plantName!.localeCompare(b.plantName!) : a.order > b.order ? 1 : -1;
                });
                setCCGTdetailsRealTimeAll(CCGTdetailsRealTimeAPI);

                let allDates = ([] as TimeSeriesItem[]).concat(CCGTdetailsRealTimeAPI[0].series!.pv ?? [],
                    CCGTdetailsRealTimeAPI[0].series!.psMax1 ?? [], CCGTdetailsRealTimeAPI[0].series!.psMax2 ?? [], CCGTdetailsRealTimeAPI[0].series!.psMin1 ?? [], CCGTdetailsRealTimeAPI[0].series!.psMin2 ?? [],
                    CCGTdetailsRealTimeAPI[0].series!.pf ?? [], CCGTdetailsRealTimeAPI[0].series!.pFfromDWH ?? [], CCGTdetailsRealTimeAPI[0].series!.prodotto ?? [], CCGTdetailsRealTimeAPI[0].series!.pvm ?? [])
                    .map(v => DateTime.fromISO(v.dateKey! as unknown as string));
                setZoom({
                    x: [DateTime.min(...allDates),
                        DateTime.max(...allDates)],
                        y: [0, CCGTdetailsRealTimeAPI[0].fullScale]
                });
                
                let MSDobj0: any = CCGTdetailsRealTimeAPI.map((ccgtDetailsRealTime: any) => {
                    let map = new Map<string, string>();
                    ccgtDetailsRealTime.msDreport!.forEach((msd: any) => {
                        map.set(msd.label, msd.status)
                    });
                    let obj: any = {'plantName': ccgtDetailsRealTime.plantName};
                    map.forEach((value, key) => {
                        obj[key] = value
                    });
                    return obj;
                });
                setMSDobj(MSDobj0);
                context.setInProgressApi!(false);
            }).catch((e) => {
                context.setInProgressApi!(false);
                if (e instanceof InteractionRequiredAuthError) {
                    context.doAcquireTokenRedirect();
                }
                else{
                    console.log(e);
                }
            }).finally(setFinished);
        }
    }
}

export default CCGTPage;